import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keep an element at the tallest height it has had, so content below it (e.g. pagination) stays in place
 * when the next page is shorter. Starts over when the reset key changes (e.g. a new search) and when the
 * window width changes (e.g. the screen is turned, which changes the number of columns)
 * @param {unknown} resetKey Value that starts over when changed
 * @returns {[function, object]} Callback ref for the element, and the style to give it
 */
const useStableHeight = (resetKey?: unknown): [(element: HTMLElement | null) => void, { minHeight?: number }] => {
  const [minHeight, setMinHeight] = useState<number>();
  const element = useRef<HTMLElement | null>(null);
  const observer = useRef<ResizeObserver | null>(null);

  // Remove the min-height at once (not only on the next render), so the next measurement is the natural height
  const reset = useCallback(() => {
    element.current && (element.current.style.minHeight = "");
    setMinHeight(undefined);
  }, []);

  useEffect(reset, [resetKey, reset]);

  useEffect(() => {
    let width = window.innerWidth;
    const onResize = () => {
      if (window.innerWidth === width) { return; }
      width = window.innerWidth;
      reset();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [reset]);

  const ref = useCallback((node: HTMLElement | null) => {
    observer.current?.disconnect();
    element.current = node;
    if (!node) { return; }
    observer.current = new ResizeObserver(() => {
      // Exact height (with fractions), so rounding does not let the element shrink by a pixel
      const height = node.getBoundingClientRect().height;
      setMinHeight(prev => prev && prev >= height ? prev : height);
    });
    observer.current.observe(node);
  }, []);

  useEffect(() => () => observer.current?.disconnect(), []);

  return [ref, { minHeight }];
}

export default useStableHeight;
